import { useEffect } from 'react';
import { useLocation, useNavigate } from '@/lib/router-compat';

interface Props {
  /** The canonical SEO slug for the current listing (make-model-year-city-uuid) */
  canonicalSlug: string;
}

/**
 * If the visitor landed on an old UUID-only URL (or a stale slug),
 * silently rewrite the address bar to the canonical SEO slug.
 * Uses replace so the back button stays clean and no reload happens.
 */
export default function CanonicalSlugSync({ canonicalSlug }: Props) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!canonicalSlug) return;
    const match = location.pathname.match(/^(.*)\/listing\/([^/]+)\/?$/);
    if (!match) return;
    const [, prefix, current] = match;
    if (current === canonicalSlug) return;

    // Only rewrite when the URL points at the SAME listing we have loaded.
    // While navigating to another listing the old data is still mounted for a
    // tick — rewriting then would bounce the user back to the previous car.
    const uuid = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
    const currentId = current.match(uuid)?.[0]?.toLowerCase();
    const canonicalId = canonicalSlug.match(uuid)?.[0]?.toLowerCase();
    if (currentId && canonicalId && currentId !== canonicalId) return;

    navigate(`${prefix}/listing/${canonicalSlug}${location.search}${location.hash}`, { replace: true });
  }, [canonicalSlug, location.pathname, location.search, location.hash, navigate]);


  return null;
}
